/**
 * Persistierter Set-Tracker-Store. Eine Singleton-Instanz pro Browser-Tab.
 * Storage-Schema: `progress:v1:YYYY-MM-DD:<exerciseId>` → JSON `number[]` (1-basierte Set-Indices).
 * Faellt auf In-Memory zurueck, falls localStorage blockiert ist (Privatemodus, Quota, SSR).
 *
 * Cloud-Sync: optional ueber `progressRepository`. localStorage bleibt die unmittelbare
 * Quelle der Wahrheit fuer die UI; das Repository hebt Aenderungen im Hintergrund nach
 * Supabase, sobald online + eingeloggt.
 */

import {
  initRepository,
  recordChange,
  type ProgressEntry,
} from './progressRepository';

const STORAGE_PREFIX = 'progress:v1:';
const EMPTY: readonly number[] = Object.freeze([]);

export function dateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function progressKey(dayKey: string, exerciseId: string): string {
  return `${dayKey}:${exerciseId}`;
}

interface Store {
  data: Map<string, readonly number[]>;
  listeners: Set<() => void>;
  isPersistent: boolean;
}

let store: Store | null = null;
let syncBootstrapped = false;

function probeLocalStorage(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    const probe = '__neoolymp_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function hydrateFromStorage(data: Map<string, readonly number[]>): void {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      try {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.every((n) => typeof n === 'number')) {
          data.set(k.slice(STORAGE_PREFIX.length), Object.freeze(parsed.slice()));
        }
      } catch {
        // Ignoriere defekte Eintraege
      }
    }
  } catch {
    // localStorage waehrend Iteration weg — kein Problem
  }
}

function getStore(): Store {
  if (store) return store;
  const data = new Map<string, readonly number[]>();
  const isPersistent = probeLocalStorage();
  if (isPersistent) hydrateFromStorage(data);
  store = { data, listeners: new Set<() => void>(), isPersistent };
  return store;
}

function notify(s: Store): void {
  for (const cb of s.listeners) cb();
}

function persist(s: Store, key: string, value: readonly number[] | null): void {
  if (!s.isPersistent) return;
  try {
    if (value === null) {
      localStorage.removeItem(STORAGE_PREFIX + key);
    } else {
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
    }
  } catch {
    // Quota voll oder Storage weg — degradiere zu In-Memory.
    s.isPersistent = false;
  }
}

export function getDoneSets(dayKey: string, exerciseId: string): readonly number[] {
  return getStore().data.get(progressKey(dayKey, exerciseId)) ?? EMPTY;
}

export function toggleSet(dayKey: string, exerciseId: string, n: number): void {
  const s = getStore();
  const key = progressKey(dayKey, exerciseId);
  const current = s.data.get(key) ?? EMPTY;
  const setCopy = new Set(current);
  if (setCopy.has(n)) {
    setCopy.delete(n);
  } else {
    setCopy.add(n);
  }
  let nextSets: readonly number[];
  if (setCopy.size === 0) {
    s.data.delete(key);
    persist(s, key, null);
    nextSets = EMPTY;
  } else {
    const next = Object.freeze([...setCopy].sort((a, b) => a - b));
    s.data.set(key, next);
    persist(s, key, next);
    nextSets = next;
  }
  notify(s);
  recordChange(dayKey, exerciseId, nextSets);
}

export function resetExercise(dayKey: string, exerciseId: string): void {
  const s = getStore();
  const key = progressKey(dayKey, exerciseId);
  if (!s.data.has(key)) return;
  s.data.delete(key);
  persist(s, key, null);
  notify(s);
  recordChange(dayKey, exerciseId, EMPTY);
}

export function isPersistent(): boolean {
  return getStore().isPersistent;
}

export function subscribe(cb: () => void): () => void {
  const s = getStore();
  s.listeners.add(cb);
  return () => {
    s.listeners.delete(cb);
  };
}

/**
 * Server-Snapshot in den lokalen Store einarbeiten (wird vom progressRepository
 * nach erfolgreichem Login + Pull aufgerufen). Schreibt sowohl in den
 * In-Memory-Store als auch in localStorage und benachrichtigt Subscriber.
 *
 * Strategie: der uebergebene Snapshot ist bereits das Merge-Ergebnis (server gewinnt
 * bei Konflikten). Wir ueberschreiben den lokalen State damit komplett, behalten
 * aber lokale Eintraege, die im Snapshot nicht enthalten sind (sollte nach Merge
 * nicht passieren, aber als Sicherheitsnetz gegen partielle Pulls).
 */
export function applyRemoteSnapshot(entries: readonly ProgressEntry[]): void {
  const s = getStore();
  const seen = new Set<string>();
  for (const e of entries) {
    const k = progressKey(e.dayKey, e.exerciseId);
    seen.add(k);
    if (e.sets.length === 0) {
      s.data.delete(k);
      persist(s, k, null);
    } else {
      const frozen = Object.freeze(e.sets.slice());
      s.data.set(k, frozen);
      persist(s, k, frozen);
    }
  }
  notify(s);
}

/**
 * Initialisiert den Cloud-Sync. Idempotent. Wird einmal beim App-Start gerufen
 * (siehe AppShell). Im Anonym-Modus (kein Supabase konfiguriert) ist das ein No-op
 * im Praxisverhalten — initRepository registriert dann nur lokale Listener.
 */
export function bootstrapSync(): void {
  if (syncBootstrapped) return;
  syncBootstrapped = true;
  initRepository(applyRemoteSnapshot);
}

/**
 * Test-Helper. NICHT im App-Code verwenden.
 */
export function __resetStoreForTests(): void {
  store = null;
  syncBootstrapped = false;
}
